import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  acceptVendorInvite,
  validateVendorInvite,
  type VendorInviteValidation,
} from "@/features/marketplace/api/vendor-invite.api";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { ApiError } from "@/lib/api-client";
import {
  AdgeLogo,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nexus/ui";

export const Route = createFileRoute("/marketplace/vendor-invite")({
  validateSearch: (search: Record<string, unknown>) => ({
    token: search.token != null ? String(search.token) : "",
  }),
  component: VendorInvitePage,
});

type PageStep = "loading" | "ready" | "accepting" | "accepted" | "error";

function VendorInvitePage() {
  const { t } = useTranslation();
  useDocumentTitle(t("page.title.vendorInvite"));
  const { token } = Route.useSearch();

  const [step, setStep] = useState<PageStep>("loading");
  const [invite, setInvite] = useState<VendorInviteValidation | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setErrorMessage(t("marketplace.vendorInvite.missingToken"));
      setStep("error");
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const data = await validateVendorInvite(token);
        if (cancelled) return;
        setInvite(data);
        setStep("ready");
      } catch (err) {
        if (cancelled) return;
        setErrorMessage(
          err instanceof ApiError && err.status === 404
            ? t("marketplace.vendorInvite.invalid")
            : t("marketplace.vendorInvite.loadFailed"),
        );
        setStep("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, t]);

  async function handleAccept() {
    if (!token) return;
    setStep("accepting");
    try {
      await acceptVendorInvite(token);
      setStep("accepted");
    } catch (err) {
      setErrorMessage(
        err instanceof ApiError && err.status === 404
          ? t("marketplace.vendorInvite.invalid")
          : t("marketplace.vendorInvite.acceptFailed"),
      );
      setStep("error");
    }
  }

  if (step === "loading" || step === "accepting") {
    return (
      <InviteShell>
        <Card className="w-full max-w-lg">
          <CardHeader className="space-y-4 text-center">
            <BrandHeader />
            <CardTitle>{t("marketplace.vendorInvite.title")}</CardTitle>
            <CardDescription>
              {step === "accepting"
                ? t("marketplace.vendorInvite.accepting")
                : t("common.loading")}
            </CardDescription>
          </CardHeader>
        </Card>
      </InviteShell>
    );
  }

  if (step === "error") {
    return (
      <InviteShell>
        <Card className="w-full max-w-lg">
          <CardHeader className="space-y-4 text-center">
            <BrandHeader />
            <CardTitle>{t("marketplace.vendorInvite.errorTitle")}</CardTitle>
            <CardDescription className="text-balance">{errorMessage}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-center text-xs text-muted-foreground">
              {t("marketplace.vendorInvite.errorHelp")}
            </p>
          </CardContent>
        </Card>
      </InviteShell>
    );
  }

  if (step === "accepted") {
    return (
      <InviteShell>
        <Card className="w-full max-w-lg">
          <CardHeader className="space-y-4 text-center">
            <BrandHeader />
            <CardTitle>{t("marketplace.vendorInvite.acceptedTitle")}</CardTitle>
            <CardDescription className="text-balance">
              {t("marketplace.vendorInvite.acceptedDescription", {
                clientName: invite?.clientName ?? "",
              })}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              {t("marketplace.vendorInvite.marketplaceNote", {
                clientName: invite?.clientName ?? "",
              })}
            </p>
            <p>
              {t("marketplace.vendorInvite.nextSteps", {
                clientName: invite?.clientName ?? "",
              })}
            </p>
          </CardContent>
        </Card>
      </InviteShell>
    );
  }

  const expiry = invite
    ? new Date(invite.expiresAt).toLocaleDateString(undefined, {
        dateStyle: "long",
      })
    : "";

  return (
    <InviteShell>
      <Card className="w-full max-w-lg">
        <CardHeader className="space-y-4 text-center">
          <BrandHeader />
          <CardTitle>
            {t("marketplace.vendorInvite.heading", { clientName: invite?.clientName ?? "" })}
          </CardTitle>
          <CardDescription className="text-balance">
            {t("marketplace.vendorInvite.intro", {
              clientName: invite?.clientName ?? "",
            })}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <dl className="space-y-2 rounded-lg border border-border/60 bg-muted/30 p-4 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t("marketplace.vendorInvite.summaryInvitedBy")}</dt>
              <dd className="font-medium text-right">{invite?.clientName}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t("marketplace.vendorInvite.summaryEmail")}</dt>
              <dd className="font-medium text-right break-all">{invite?.email}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t("marketplace.vendorInvite.summaryExpires")}</dt>
              <dd className="font-medium text-right">{expiry}</dd>
            </div>
          </dl>

          <p className="text-sm text-muted-foreground">
            {t("marketplace.vendorInvite.marketplaceNote", {
              clientName: invite?.clientName ?? "",
            })}
          </p>

          <Button className="w-full" onClick={() => void handleAccept()}>
            {t("marketplace.vendorInvite.acceptAction")}
          </Button>

          <p className="text-center text-xs text-muted-foreground">
            {t("marketplace.vendorInvite.footnote", {
              clientName: invite?.clientName ?? "",
              expiry,
            })}
          </p>
        </CardContent>
      </Card>
    </InviteShell>
  );
}

function InviteShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-svh items-center justify-center bg-background p-4">
      {children}
    </div>
  );
}

function BrandHeader() {
  const { t } = useTranslation();
  return (
    <div className="flex justify-center">
      <AdgeLogo aria-label={t("auth.brandName")} />
    </div>
  );
}
